import { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Chip, FormControl, LinearProgress, MenuItem, Pagination, Paper, Select, SelectChangeEvent, Snackbar, SnackbarCloseReason, Table, TableBody, TableCell, TableContainer, TableFooter, TableHead, TableRow, Tooltip, Typography, useMediaQuery, useTheme, alpha } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { LayoutBaseDePagina } from '../../../shared/layouts';
import { LinhasEsqueletoTabela, paperListagemSx, tableContainerListagemSx, valorMonetarioSx } from '../../../shared/components';
import { Environment } from '../../../shared/environment';
import { useListagemAltura, useListagemKeyboardNav } from '../../../shared/hooks';
import dayjs, { Dayjs } from 'dayjs';
import { FerramentasDaListagemFinanceiroEntreContas } from '../../../shared/components';
import { IListagem, LancamentoEntreConstasService } from '../../../shared/services/api/Financeiro/LancamentoEntreConstas';
import { brand } from '../../../shared/themes';
import SwapHorizRoundedIcon from '@mui/icons-material/SwapHorizRounded';
import { CelulaListaMobile, LinhaListagemMobile, ListaMobile, useCelular, moedaMobile } from '../../../shared/components/listagem-mobile';

const OPCOES_POR_PAGINA = [10, 25, 50, 100];

export const ListagemEntreContas: React.FC = () => {
  const listaCelular = useCelular();
  const [searchParams, setSearchParams] = useSearchParams();
  const [rows, setRows] = useState<IListagem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [dataInicial, setDataInicial] = useState('');
  const [dataFinal, setDataFinal] = useState('');
  const navigate = useNavigate();
  const theme = useTheme();
  // No celular esconde Nº Documento/Conta de Débito (secundárias) em vez de deixar a
  // tabela rolar na horizontal — cabe tudo na tela só com Data/Conta Crédito/Valor.
  const smDown = useMediaQuery(theme.breakpoints.down('sm'));
  const [openmsg, setOpenmsg] = useState(false);
  const [msg, setMsg] = useState('');
  const [tms, setTms] = useState<'warning' | 'error' | 'info' | 'success'>('warning');
  const ROWS_CACHE_KEY = 'entrecontas';

  const { tabelaRef, containerHeight, rowsPerPage, setRowsPerPage } = useListagemAltura({
    cacheKey: ROWS_CACHE_KEY,
    valorInicial: Environment.LIMITE_DE_LINHAS,
    rowHeight: 48,
  });

  const handleChangeRowsPerPage = (event: SelectChangeEvent<number>) => {
    setRowsPerPage(Number(event.target.value));
  };

  const handleCloseMsg = (
    event?: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason,
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenmsg(false);
  };

  const pagina = useMemo(() => {
    return Number(searchParams.get('pagina') || '1');
  }, [searchParams]);

  useEffect(() => {
    if (dataInicial !== '') {
      setIsLoading(true);
      LancamentoEntreConstasService.getAll(pagina, dataInicial, dataFinal, rowsPerPage)
        .then((result) => {
          setIsLoading(false);
          if (result instanceof Error) {
            setTms('error');
            setMsg(result.message);
            setOpenmsg(true);
          } else {
            setTotalCount(result.totalCount);
            setRows(result.data);
          }
        });
    }
  }, [pagina, rowsPerPage]);

  const handlePesquisa = (datainicial: Dayjs | null, datafinal: Dayjs | null) => {
    const formattedDateInicial = datainicial ? dayjs(datainicial).format('DD/MM/YYYY') : '';
    const formattedDateFinal = datafinal ? dayjs(datafinal).format('DD/MM/YYYY') : '';
    setDataInicial(formattedDateInicial);
    setDataFinal(formattedDateFinal);
    setIsLoading(true);
    LancamentoEntreConstasService.getAll(1, formattedDateInicial, formattedDateFinal, rowsPerPage)
      .then((result) => {
        setIsLoading(false);
        if (result instanceof Error) {
          setTms('error');
          setMsg(result.message);
          setOpenmsg(true);
        } else {
          setTotalCount(result.totalCount);
          setRows(result.data);
        }
      });
  };

  const primeiroItem = totalCount === 0 ? 0 : (pagina - 1) * rowsPerPage + 1;
  const ultimoItem = Math.min(pagina * rowsPerPage, totalCount);
  const totalPaginas = Math.max(1, Math.ceil(totalCount / rowsPerPage));

  // Sem F8/refoco de busca aqui: a pesquisa é por um botão explícito de datas (não um campo de
  // texto único), então não há input pra focar automaticamente.
  const { linhaRefCallback, onLinhaKeyDown } = useListagemKeyboardNav({
    rowsLength: rows.length,
    pagina,
    totalPaginas,
    onIrParaPagina: (novaPagina) => setSearchParams({ pagina: novaPagina.toString() }, { replace: true }),
    onEnter: (index) => navigate(`/entrecontas/${rows[index].idlan}`),
    rowsDep: [rows],
  });

  return (
    <LayoutBaseDePagina
      titulo='Lançamento Entre Contas'
      subtitulo='Visualize, pesquise e gerencie todos os lançamentos entre contas cadastrados no sistema.'
      barraDeFerramentas={
        <FerramentasDaListagemFinanceiroEntreContas
          mostrarInputData={true}
          aoClicarEmPesquisa={handlePesquisa}
          aoClicarEmNovo={() => navigate('/entrecontas/novo')}
        />
      }
    >
      <Snackbar anchorOrigin={{ vertical: 'top', horizontal: 'center' }} open={openmsg} autoHideDuration={tms === 'error' ? null : 900} onClose={handleCloseMsg}>
        <Alert
          onClose={handleCloseMsg}
          severity={tms}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {msg}
        </Alert>
      </Snackbar>

      <Paper elevation={0} sx={paperListagemSx(theme)}>
        <TableContainer
          ref={tabelaRef}
          sx={tableContainerListagemSx(containerHeight, isLoading || rows.length >= rowsPerPage)}
        >
          {/* No desktop, minWidth (não width:'100%') evita que tableLayout:'fixed'
              espreme as colunas abaixo do legível — quem rola é o TableContainer. No
              celular (smDown) Nº Documento/Conta de Débito somem (ver abaixo), então
              width:'100%' sozinho cabe as 3 colunas restantes na tela. */}
          <Table size='small' sx={{ width: '100%', minWidth: smDown ? 0 : 750, tableLayout: 'fixed' }}>
            {!listaCelular && (
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: smDown ? '92px' : '15%' }}>{smDown ? 'Data' : 'Data Lançamento'}</TableCell>
                {!smDown && <TableCell sx={{ width: '15%' }}>Nº Documento</TableCell>}
                <TableCell sx={{ width: smDown ? undefined : '25%' }}>Conta Crédito</TableCell>
                {!smDown && <TableCell sx={{ width: '25%' }}>Conta de Débito</TableCell>}
                <TableCell align='right' sx={{ width: smDown ? '110px' : '20%' }}>Valor</TableCell>
              </TableRow>
            </TableHead>
            )}

            {isLoading && rows.length === 0 && (
              <TableBody>
                <LinhasEsqueletoTabela
                  colunas={smDown ? [
                    { width: '65%' },
                    { width: '75%' },
                    { width: '55%', align: 'right' },
                  ] : [
                    { width: '65%' },
                    { width: '55%' },
                    { width: '75%' },
                    { width: '75%' },
                    { width: '55%', align: 'right' },
                  ]}
                />
              </TableBody>
            )}
            <TableBody>
              {listaCelular ? (
              <CelulaListaMobile>
                <ListaMobile
                  linhas={rows}
                  chave={(row) => row.idlan}
                  renderizar={(row) => (
                    <LinhaListagemMobile
                      titulo={`${row.saida} → ${row.entrada}`}
                      detalhes={[dayjs(row.datalan).format('DD/MM/YY'), row.numero ? `Nº ${row.numero}` : null]}
                      valor={moedaMobile(row.valor)}
                      etiqueta={row.revertido ? { rotulo: 'Revertido', cor: 'warning' } : undefined}
                      onClick={() => navigate(`/entrecontas/${row.idlan}`)}
                    />
                  )}
                />
              </CelulaListaMobile>
            ) : rows.map((row, index) => (
                <TableRow
                  key={row.idlan}
                  ref={linhaRefCallback(index)}
                  hover
                  tabIndex={0}
                  onClick={() => navigate(`/entrecontas/${row.idlan}`)}
                  onKeyDown={onLinhaKeyDown(index)}
                  sx={{
                    cursor: 'pointer',
                    outline: 'none',
                    '&:last-child td': { borderBottom: 0 },
                    '& td': {
                      py: smDown ? 0.75 : 1.25,
                      px: smDown ? 0.75 : 1.5,
                      fontSize: smDown ? '0.75rem' : '0.8125rem',
                      borderBottom: `1px solid ${theme.torq.border.default}`,
                    },
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.035),
                    },
                    '&:focus-visible': {
                      outline: `2px solid ${theme.torq.border.focus}`,
                      outlineOffset: '-2px',
                      backgroundColor: alpha(theme.palette.primary.main, 0.05),
                    },
                  }}
                >
                  <TableCell>
                    <Typography noWrap sx={{ fontSize: '0.8125rem', color: theme.torq.text.primary }}>
                      {dayjs(row.datalan).format('DD/MM/YYYY')}
                    </Typography>
                  </TableCell>
                  {!smDown && (
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, overflow: 'hidden' }}>
                        <Typography noWrap sx={{ fontSize: '0.8125rem', color: theme.torq.text.secondary }}>
                          {row.numero}
                        </Typography>
                        {row.revertido && (
                          <Tooltip
                            title={row.revertidoem ? `Revertido em ${dayjs(row.revertidoem).format('DD/MM/YYYY [às] HH:mm')}` : 'Revertido'}
                            arrow
                          >
                            <Chip
                              label="Revertido"
                              size="small"
                              color="default"
                              sx={{ height: '16px', fontSize: '0.55rem', flexShrink: 0 }}
                            />
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  )}
                  <TableCell>
                    <Typography fontWeight={600} noWrap sx={{ fontSize: '0.8125rem', color: theme.torq.text.primary, textDecoration: row.revertido ? 'line-through' : 'none', opacity: row.revertido ? 0.5 : 1 }}>
                      {row.entrada}
                    </Typography>
                  </TableCell>
                  {!smDown && (
                    <TableCell>
                      <Typography fontWeight={600} noWrap sx={{ fontSize: '0.8125rem', color: theme.torq.text.primary, textDecoration: row.revertido ? 'line-through' : 'none', opacity: row.revertido ? 0.5 : 1 }}>
                        {row.saida}
                      </Typography>
                    </TableCell>
                  )}
                  <TableCell align='right'>
                    <Typography sx={{ ...valorMonetarioSx, textDecoration: row.revertido ? 'line-through' : 'none', opacity: row.revertido ? 0.5 : 1 }}>
                      {row.valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>

            {rows.length === 0 && !isLoading && (
              <TableBody>
                <TableRow>
                  <TableCell colSpan={smDown ? 3 : 5} align='center' sx={{ py: 3 }}>
                    <Box>
                      <SwapHorizRoundedIcon sx={{
                        fontSize: 35,
                        color: theme.palette.mode === 'dark'
                          ? alpha(theme.palette.text.secondary, 0.4)
                          : 'text.secondary',
                        mb: 1
                      }} />
                      <Typography variant='h6' color='text.secondary' gutterBottom fontSize="0.85rem">
                        {Environment.LISTAGEM_VAZIA}
                      </Typography>
                      <Typography variant='body2' color='text.secondary' fontSize="0.7rem">
                        {dataInicial ? 'Tente ajustar o período de busca' : 'Selecione um período e clique em pesquisar'}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              </TableBody>
            )}

            <TableFooter>
              {isLoading && (
                <TableRow>
                  <TableCell colSpan={smDown ? 3 : 5} sx={{ p: 0 }}>
                    <LinearProgress sx={{ height: '1.5px' }} />
                  </TableCell>
                </TableRow>
              )}
              <TableRow>
                <TableCell colSpan={smDown ? 3 : 5} sx={{ py: 1.25, px: 2, borderTop: `1px solid ${theme.torq.border.default}` }}>
                  <Box display='flex' alignItems='center' justifyContent='space-between' flexWrap='wrap' gap={1.5}>
                    <Typography variant='caption' color='text.secondary'>
                      {totalCount === 0
                        ? 'Nenhum lançamento encontrado'
                        : `Mostrando de ${primeiroItem} a ${ultimoItem} de ${totalCount} lançamento${totalCount !== 1 ? 's' : ''}`}
                    </Typography>

                    {totalCount > rowsPerPage && (
                      <Pagination
                        page={pagina}
                        count={totalPaginas}
                        onChange={(_, newPage) => setSearchParams({ pagina: newPage.toString() }, { replace: true })}
                        size="small"
                        shape="rounded"
                        showFirstButton
                        showLastButton
                        siblingCount={1}
                        boundaryCount={1}
                        sx={{
                          '& .MuiPaginationItem-root.Mui-selected': {
                            backgroundColor: brand.menuBase,
                            color: '#fff',
                            '&:hover': { backgroundColor: '#012D3D' },
                          },
                        }}
                      />
                    )}

                    <FormControl size='small' sx={{ minWidth: 110 }}>
                      <Select<number> value={rowsPerPage} onChange={handleChangeRowsPerPage}>
                        {(OPCOES_POR_PAGINA.includes(rowsPerPage) ? OPCOES_POR_PAGINA : [...OPCOES_POR_PAGINA, rowsPerPage].sort((a, b) => a - b)).map((opcao) => (
                          <MenuItem key={opcao} value={opcao}>{opcao} / página</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                </TableCell>
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      </Paper>
    </LayoutBaseDePagina>
  );
};